import * as React from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import {
  BulkCreateWorkSchedulePlanningVersionsDocument,
  WorkScheduleEmployeesDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import DayPicker from 'modules/core/components/Inputs/DayPicker'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useMutation } from 'modules/core/hooks/useMutation'
import { dateToString, dateUIToBackend } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelector } from 'modules/employees/components/EmployeeSelector'
import type WorkSchedule from 'modules/work_schedules/store/models/Schedule'

const AddEmployeesModal = ({
  workSchedule,
  onClose,
  onSuccess,
}: {
  workSchedule: WorkSchedule
  onClose: () => void
  onSuccess: () => void
}) => {
  const [selectedEmployees, setSelectedEmployees] = React.useState<
    EmployeeForSelectFragmentNoAccess[]
  >([])

  // get employees already assigned to this work schedule
  const currentEmployeesIds = workSchedule.employeeIds

  const form = useLazyRef<Form>(
    () =>
      new Form(
        {
          effective_at: dateToString(moment(), 'backend'),
        },
        {
          effective_at: 'date',
        }
      )
  ).current

  const [bulkCreateWorkSchedulePlanningVersions, { loading: _loading }] = useMutation(
    BulkCreateWorkSchedulePlanningVersionsDocument,
    {
      refetchQueries: [WorkScheduleEmployeesDocument],
      extractorFn: (data) => data.timePlanningMutations.bulkCreateTimeplanningPlanningVersion,
    }
  )

  const { running, performAction: onSubmit } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      await bulkCreateWorkSchedulePlanningVersions({
        variables: {
          employeeIds: selectedEmployees.map((employee) => employee.id),
          effectiveAt: dateUIToBackend(form.get('effective_at').value),
          scheduleId: workSchedule.attributes.id,
        },
      })
      onSuccess()
      closePortal()
      onClose()
    }
  )

  return (
    <Modal
      illustration="calendar-clock"
      title={i18n.t('settings.work_schedule.employees_tab.employee_modal.assign')}
      description={i18n.t('settings.work_schedule.employees_tab.employee_modal.description')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('settings.work_schedule.cancel')}
          </Button>
          <Button disabled={running} submit>
            {i18n.t('settings.work_schedule.employees_tab.employee_modal.assign')}
          </Button>
        </>
      }
    >
      <EmployeeSelector
        alwaysOpen
        excludeIds={currentEmployeesIds}
        onSelect={setSelectedEmployees}
        multiple
        value={selectedEmployees}
      />

      <Box paddingY="s16">
        <DayPicker
          field={form.get('effective_at')}
          label={i18n.t('settings.work_schedule.employees_tab.table.header.date')}
        />
      </Box>
      <Text.Normal>
        {i18n.t('settings.work_schedule.employees_tab.employee_modal.footer_description')}
      </Text.Normal>
    </Modal>
  )
}

export default observer(AddEmployeesModal)
